import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Calculator, Lock, Unlock, CheckCircle2, AlertTriangle } from "lucide-react";
import { monthLabel, MONTHS, BATCH_STATUS } from "@/pages/hr/payroll/payrollUtils";
import BatchDetail from "@/pages/hr/payroll/BatchDetail";

export default function PayrollBatchesTab({ employees }) {
  const { can } = useAuth();
  const [batches, setBatches] = useState([]);
  const [creating, setCreating] = useState(null);
  const [openBatch, setOpenBatch] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/hr/payroll/batches").then((r) => setBatches(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const createBatch = async () => {
    if (!creating.year || !creating.month) return toast.error("Year and month are required");
    setBusy(true);
    try {
      await api.post("/hr/payroll/batches", { year: Number(creating.year), month: Number(creating.month), name: creating.name || undefined });
      toast.success("Payroll batch created"); setCreating(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const act = async (b, action, body) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/hr/payroll/batches/${b.id}/${action}`, body || {});
      if (action === "calculate") toast.success(`Calculated ${data.employees} employee(s) · ${data.requires_review} need review`);
      else toast.success("Done");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const reopen = async (b) => {
    const reason = window.prompt("Reopen finalized payroll — audited reason:");
    if (!reason) return;
    await act(b, "reopen", { reason });
  };

  if (openBatch) {
    return <BatchDetail batchId={openBatch} employees={employees} onBack={() => { setOpenBatch(null); load(); }} />;
  }

  return (
    <div data-testid="payroll-batches-tab">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-slate-500">Monthly payroll batches — Draft → Calculated → Reviewed → Finalized (locked).</p>
        {can("payroll:process") && (
          <Button size="sm" className="bg-slate-900" data-testid="payroll-create-batch-btn"
            onClick={() => setCreating({ year: new Date().getFullYear(), month: "", name: "" })}>
            <Plus className="w-4 h-4 mr-1.5" />New Batch
          </Button>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="excel-table w-full" data-testid="payroll-batches-table">
          <thead><tr><th>Period</th><th>Name</th><th>Mode</th><th>Status</th><th>Employees</th><th>Needs Review</th><th>Actions</th></tr></thead>
          <tbody>
            {batches.length === 0 ? <tr><td colSpan={7} className="text-center text-slate-400 py-6">No payroll batches yet. Create one to begin.</td></tr>
              : batches.map((b) => {
                const st = BATCH_STATUS[b.status] || { label: b.status, cls: "" };
                return (
                  <tr key={b.id} data-testid={`payroll-batch-row-${b.year}-${b.month}`}>
                    <td className="font-medium">{monthLabel(b.year, b.month)}</td>
                    <td className="text-xs">{b.name}</td>
                    <td><Badge variant="outline" className={b.mode === "shadow" ? "text-amber-700 border-amber-300" : "text-emerald-700 border-emerald-300"}>{b.mode}</Badge></td>
                    <td><Badge variant="outline" className={st.cls}>{st.label}</Badge></td>
                    <td className="text-center">{b.employee_count ?? "—"}</td>
                    <td className="text-center">{b.review_count ? <span className="text-amber-600 font-semibold flex items-center justify-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />{b.review_count}</span> : (b.status === "draft" ? "—" : "0")}</td>
                    <td>
                      <div className="flex gap-1.5 flex-wrap">
                        <Button size="sm" variant="outline" data-testid={`payroll-open-batch-${b.id}`} onClick={() => setOpenBatch(b.id)}>Open</Button>
                        {can("payroll:process") && b.status !== "finalized" && (
                          <Button size="sm" variant="outline" disabled={busy} data-testid={`payroll-calc-${b.id}`} onClick={() => act(b, "calculate")}>
                            <Calculator className="w-3.5 h-3.5 mr-1" />{b.status === "draft" ? "Calculate" : "Recalculate"}
                          </Button>
                        )}
                        {can("payroll:review") && (b.status === "calculated") && (
                          <Button size="sm" variant="outline" disabled={busy} data-testid={`payroll-review-${b.id}`} onClick={() => act(b, "review")}>
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Mark Reviewed
                          </Button>
                        )}
                        {can("payroll:finalize") && (b.status === "calculated" || b.status === "reviewed") && (
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={busy} data-testid={`payroll-finalize-${b.id}`} onClick={() => act(b, "finalize")}>
                            <Lock className="w-3.5 h-3.5 mr-1" />{b.mode === "shadow" ? "Shadow Finalize" : "Finalize"}
                          </Button>
                        )}
                        {can("payroll:finalize") && (b.status === "finalized" || b.status === "shadow_finalized") && (
                          <Button size="sm" variant="outline" className="text-rose-600 border-rose-300" disabled={busy} data-testid={`payroll-reopen-${b.id}`} onClick={() => reopen(b)}>
                            <Unlock className="w-3.5 h-3.5 mr-1" />Reopen
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {creating && (
        <Dialog open onOpenChange={(v) => !v && setCreating(null)}>
          <DialogContent className="max-w-sm" data-testid="payroll-create-dialog">
            <DialogHeader><DialogTitle>New Payroll Batch</DialogTitle>
              <DialogDescription>2026 batches run in shadow mode (compared vs AutoCount).</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Year</Label><Input type="number" value={creating.year} onChange={(e) => setCreating({ ...creating, year: e.target.value })} data-testid="batch-year-input" /></div>
                <div><Label className="text-xs">Month</Label>
                  <Select value={String(creating.month)} onValueChange={(v) => setCreating({ ...creating, month: v })}>
                    <SelectTrigger data-testid="batch-month-select"><SelectValue placeholder="Month" /></SelectTrigger>
                    <SelectContent>{MONTHS.slice(1).map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
              <div><Label className="text-xs">Name (optional)</Label><Input value={creating.name} onChange={(e) => setCreating({ ...creating, name: e.target.value })} placeholder="e.g. October 2026 Payroll" data-testid="batch-name-input" /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setCreating(null)}>Cancel</Button><Button className="bg-slate-900" disabled={busy} onClick={createBatch} data-testid="batch-create-submit">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
